/**
 * Re-homes transaction values saved under the legacy item-id scheme
 * (section * 1000 + table * 100 + row) into the table they really belong to.
 *
 * Legacy rows >= 100 ran into the next table's ids: purchase row 101 got 1301,
 * the id of sale row 1. Both rows were merged and saved with the "B.3:" prefix,
 * and old item 1401 (B.4 holdings) was saved among claim_fields as "B.4: ...".
 *
 * A stored value's true table is resolved, in order, from:
 *   1. its label — the only table in its section that renders that label;
 *   2. its value — the only table whose select/radio offers it
 *      (Transaction Type: P/I purchases, S/O sales);
 *   3. its item — the table every other value of the same legacy item went to;
 *   4. its prefix — the table the stored name already carries.
 * The true row is then legacy id − section * 1000 − table * 100.
 *
 * Used by loadMember (layout read from the rendered form) and by the tests
 * (layout read from the form JSON).
 */
import { decodeItemId, itemIdOf } from "./utils.js";

const SECTION_PREFIX = ["B", "C", "D"];
const LEGACY_ITEM_ID_LIMIT = 10_000;

/**
 * Strips parenthetical text, matching `transformTransactionKeys` on save.
 *
 * @param {string} label
 * @returns {string}
 */
export function cleanLabel(label) {
    return (label || "").replace(/\s*\([^)]*\)/g, "").trim();
}

/**
 * Parses a stored name such as "B.3: Total Sale Price".
 *
 * @param {string} name
 * @returns {{section: number, table: number, label: string}|null}
 */
export function parsePrefixedName(name) {
    const match = /^([BCD])\.(\d+):\s*(.*)$/.exec(name || "");
    if (!match) return null;
    return {
        section: SECTION_PREFIX.indexOf(match[1]) + 1,
        table: parseInt(match[2], 10),
        label: match[3].trim(),
    };
}

/**
 * Builds the stored name for a value in the given table.
 *
 * @param {number} section
 * @param {number} table
 * @param {string} label - Cleaned label.
 * @returns {string}
 */
function prefixedName(section, table, label) {
    return `${SECTION_PREFIX[section - 1]}.${table}: ${label}`;
}

/**
 * Layout of the transaction tables:
 *   layout[section][table][cleanedLabel] = Set of option values | null
 * (null for free-text inputs).
 *
 * @typedef {Object<number, Object<number, Object<string, Set<string>|null>>>} Layout
 */

/**
 * @param {Layout} layout
 * @param {number} section
 * @param {number} table
 * @param {string} label
 * @param {Set<string>|null} options
 */
function addToLayout(layout, section, table, label, options) {
    layout[section] = layout[section] || {};
    layout[section][table] = layout[section][table] || {};
    const current = layout[section][table][label];
    if (options && current) options.forEach((v) => current.add(v));
    else if (current === undefined) layout[section][table][label] = options;
}

/**
 * Reads the layout from a form JSON's `claim_transactions` tables, numbered
 * the way customSubmitClaim.js renders them (grouped by "part_<x>_" id).
 *
 * @param {Object} formJson - e.g. docs/individualForm.json
 * @returns {Layout}
 */
export function layoutFromForm(formJson) {
    const tables = findTransactionTables(formJson);
    const groups = {};
    tables.forEach((table) => {
        const partMatch = table.id?.match(/^(part_[a-z])_/);
        const partKey = partMatch ? partMatch[1] : "part_b";
        (groups[partKey] = groups[partKey] || []).push(table);
    });

    const layout = {};
    Object.keys(groups).forEach((partKey) => {
        const section = partKey.charCodeAt(5) - 97;
        groups[partKey].forEach((table, index) => {
            table.fields.forEach((field) => {
                const values = (field.values || [])
                    .map((v) => v.value)
                    .filter((v) => v !== "" && v != null);
                addToLayout(
                    layout,
                    section,
                    index + 1,
                    cleanLabel(field.label),
                    values.length ? new Set(values) : null
                );
            });
        });
    });
    return layout;
}

/**
 * @param {*} node
 * @returns {Array<Object>} The first `claim_transactions` table list found.
 */
function findTransactionTables(node) {
    if (Array.isArray(node)) {
        for (const child of node) {
            const found = findTransactionTables(child);
            if (found.length) return found;
        }
    } else if (node && typeof node === "object") {
        if (Array.isArray(node.claim_transactions) && node.claim_transactions.length) {
            return node.claim_transactions;
        }
        for (const child of Object.values(node)) {
            const found = findTransactionTables(child);
            if (found.length) return found;
        }
    }
    return [];
}

/**
 * Reads the layout from the rendered transaction inputs (first rows are
 * always rendered, so every table and label is present).
 *
 * @param {ParentNode} root
 * @returns {Layout}
 */
export function layoutFromDom(root) {
    const layout = {};
    root.querySelectorAll("[item-id][data-label]").forEach((el) => {
        const { section, table } = decodeItemId(el.getAttribute("item-id"));
        if (!section || !table) return;
        let options = null;
        if (el.tagName === "SELECT") {
            const values = Array.from(el.options)
                .map((o) => o.value)
                .filter((v) => v !== "");
            options = values.length ? new Set(values) : null;
        } else if (el.type === "radio" || el.type === "checkbox") {
            options = el.value ? new Set([el.value]) : null;
        }
        addToLayout(
            layout,
            section,
            table,
            cleanLabel(el.getAttribute("data-label")),
            options
        );
    });
    return layout;
}

/**
 * Re-homes stored values into their true table and row.
 *
 * Entries with a current-scheme item-id are returned unchanged. Entries from
 * claim_fields carry no item-id; a prefixed one is treated as row 1 of its
 * prefix table (its legacy id), which is where the old form put it.
 *
 * @param {Array<{name: string, value: string, item_id?: number|string|null, source?: string}>} entries
 * @param {Layout} layout
 * @returns {{
 *   entries: Array<{name: string, value: string, item_id: number|null, source?: string, legacy_item_id: number|null, moved: boolean}>,
 *   missing: Array<{item_id: number, name: string}>,
 * }} `missing` lists choice fields (e.g. Transaction Type) absent from a
 *   trade that shared its legacy id with another table — their value was
 *   overwritten on the original submission and must be re-entered.
 */
export function remapLegacyEntries(entries, layout) {
    const staged = entries.map((entry) => {
        const parsed = parsePrefixedName(entry.name);
        const id = parseInt(entry.item_id, 10);
        const legacyId =
            parsed && (!id || id < LEGACY_ITEM_ID_LIMIT)
                ? id > 1
                    ? id
                    : parsed.section * 1000 + parsed.table * 100 + 1
                : null;
        return { entry, parsed, legacyId, table: null };
    });

    // 1–2. label, then value
    staged.forEach((s) => {
        if (!s.legacyId) return;
        const tables = layout[s.parsed.section] || {};
        const label = cleanLabel(s.parsed.label);
        let candidates = Object.keys(tables)
            .map(Number)
            .filter((t) => label in tables[t]);
        if (candidates.length > 1) {
            const byValue = candidates.filter((t) =>
                tables[t][label]?.has(String(s.entry.value))
            );
            if (byValue.length) candidates = byValue;
        }
        if (candidates.length === 1) s.table = candidates[0];
    });

    // 3–4. item, then prefix
    const tablesByLegacyId = new Map();
    staged.forEach((s) => {
        if (!s.legacyId || !s.table) return;
        const set = tablesByLegacyId.get(s.legacyId) || new Set();
        set.add(s.table);
        tablesByLegacyId.set(s.legacyId, set);
    });
    staged.forEach((s) => {
        if (!s.legacyId || s.table) return;
        const set = tablesByLegacyId.get(s.legacyId);
        s.table = set && set.size === 1 ? [...set][0] : s.parsed.table;
    });

    const result = staged.map((s) => {
        const { entry, parsed, legacyId } = s;
        if (!legacyId) {
            return { ...entry, item_id: entry.item_id ?? null, legacy_item_id: null, moved: false };
        }
        const section = parsed.section;
        let row = legacyId - section * 1000 - s.table * 100;
        let table = s.table;
        if (row < 1) {
            // Not reachable from this table: keep the stored position.
            table = parsed.table;
            row = decodeItemId(legacyId).row;
        }
        return {
            ...entry,
            name: prefixedName(section, table, cleanLabel(parsed.label)),
            item_id: itemIdOf(section, table, row),
            legacy_item_id: legacyId,
            moved: table !== parsed.table,
        };
    });

    // Choice fields missing from trades whose legacy id was shared.
    const missing = [];
    tablesByLegacyId.forEach((tables, legacyId) => {
        if (tables.size < 2) return;
        const section = Math.floor(legacyId / 1000);
        tables.forEach((table) => {
            const itemId = itemIdOf(section, table, legacyId - section * 1000 - table * 100);
            const present = new Set(
                result
                    .filter((r) => r.item_id === itemId)
                    .map((r) => parsePrefixedName(r.name).label)
            );
            Object.entries(layout[section]?.[table] || {}).forEach(([label, options]) => {
                if (options && !present.has(label)) {
                    missing.push({ item_id: itemId, name: prefixedName(section, table, label) });
                }
            });
        });
    });

    return { entries: result, missing };
}
